import type { TColor, TColorKey } from '@Theme';
import type { FC } from 'react';
import React, { memo, useCallback, useState } from 'react';
import type {
  KeyboardTypeOptions,
  LayoutChangeEvent,
  NativeSyntheticEvent,
  StyleProp,
  TextInputChangeEventData,
  TextInputKeyPressEventData,
  TextInput as TextInputRN,
  ViewStyle,
} from 'react-native';
import { Platform, StyleSheet } from 'react-native';
import styled, { css } from 'styled-components/native';

import OwnableTheme from '../../assets/OwnableTheme';
import Icon from '../Common/Icons/Icon';

type TTextAlign = 'center' | 'left' | 'right';

export type TAutoCompleteOptions =
  | 'address-level1'
  | 'address-level2'
  | 'address-line-1'
  | 'address-line-2'
  | 'cc-csc'
  | 'cc-exp-month'
  | 'cc-exp-year'
  | 'cc-exp'
  | 'cc-number'
  | 'country'
  | 'email'
  | 'name'
  | 'off'
  | 'password'
  | 'postal-code'
  | 'street-address'
  | 'tel'
  | 'username'
  | undefined;

type ITextInputProps = {
  value: string;
  fontSizePx: number;
  placeholder: string;
  placeholderColor?: TColor;
  textColor?: TColor;
  onChange?: (value: string, id?: string) => void;
  secureTextEntry?: boolean;
  type?: 'email' | 'number' | 'text';
  bgColor?: TColorKey;
  color?: string;
  gutterBottom?: number;
  numberOfLines?: number;
  editable?: boolean;
  leftElement?: React.ReactNode;
  rightElement?: React.ReactNode;
  borderColor?: TColorKey | null;
  borderBottomColor?: TColor | null;
  textAlign?: TTextAlign;
  onKeyPress?: (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => void;
  customStyles?: StyleProp<ViewStyle>;
  id?: string | null;
  name?: string | null;
  autocomplete?: TAutoCompleteOptions;
  textContentType?: 'emailAddress' | 'name' | 'none' | 'password' | 'username';
  onBlur?: () => void;
  onFocus?: () => void;
  autoCapitalize?: 'characters' | 'none' | 'sentences' | 'words' | undefined;
  onLayout?: (event: LayoutChangeEvent) => void;
  textInputStyle?: StyleProp<TextInputRN>;
};

const StyledTextInput = styled.TextInput.attrs<{
  placeholderTextColor?: string;
}>(({ placeholderTextColor }) => ({
  placeholderTextColor: placeholderTextColor ?? OwnableTheme.colors.green,
}))<{
  fontSize: number;
  textAlign?: TTextAlign;
  hideCursor?: boolean;
  textColor?: TColor;
  placeholderTextColor?: string;
  color: string;
}>`
  width: 100%;
  font-family: ${OwnableTheme.fonts.gilroy.regular};
  font-size: ${({ fontSize }) => `${fontSize}px`};
  text-align: ${({ textAlign }) => textAlign ?? 'center'};
  color: ${({ textColor, color }) =>
    textColor ?? color ?? OwnableTheme.colors.persianIndigo};
  padding: 5px;
  border-color: transparent;
  ${({ hideCursor }) =>
    hideCursor &&
    Platform.OS === 'web' &&
    css`
      pointer-events: none;
    `};
`;

const StyledInlineRow = styled.View<{
  bgColor?: TColorKey;
  borderColor?: TColorKey | null;
  borderBottomColor?: TColor | null;
  numberOfLines?: number;
  gutterBottom: number;
}>`
  background-color: ${({ bgColor }) =>
    bgColor ? OwnableTheme.colors[bgColor] : 'transparent'};
  position: relative;
  flex-direction: row;
  width: 100%;
  padding: 8px 8px 4px 0;
  border-radius: ${({ numberOfLines }) =>
    numberOfLines === 1 ? '12px' : '12px'};
  margin-bottom: ${({ gutterBottom }) => `${gutterBottom}px`};
  border: ${({ borderColor }) =>
    borderColor
      ? `1px solid ${OwnableTheme.colors[borderColor]}`
      : '1px solid transparent'};
  ${({ borderBottomColor }) =>
    borderBottomColor &&
    css`
      border-bottom-width: 1px;
      border-bottom-color: ${borderBottomColor};
      border-radius: 0;
    `}
`;

const IconContainer = styled.TouchableOpacity`
  ${Platform.OS === 'web' &&
  css`
    cursor: pointer;
  `}
`;

// todo - map type (text,number, password) to sensible react native props like secureTextEntry
const TextInput: FC<ITextInputProps> = memo(
  ({
    value,
    fontSizePx,
    placeholder,
    placeholderColor,
    textColor,
    onChange,
    secureTextEntry = false,
    type = 'text',
    bgColor,
    color = OwnableTheme.colors.persianIndigo,
    textContentType = 'none',
    gutterBottom = 0,
    numberOfLines = 1,
    editable = true,
    leftElement,
    rightElement,
    borderColor,
    borderBottomColor,
    textAlign = 'center',
    customStyles = {},
    onKeyPress = () => {
      /* do nothing */
    },
    id = null,
    name = null,
    autocomplete = 'off',
    onBlur = () => {
      /* do nothing */
    },
    autoCapitalize = 'none',
    onFocus,
    onLayout,
    textInputStyle,
  }) => {
    const [showPassword, setShowPassword] = useState(false);
    const onChangeCallback = useCallback(
      ({ nativeEvent }: NativeSyntheticEvent<TextInputChangeEventData>) => {
        const newValue = nativeEvent.text;
        if (typeof onChange === 'function') {
          onChange(newValue);
        }
      },
      [onChange]
    );

    const keyboardType = (): KeyboardTypeOptions => {
      switch (type) {
        case 'number':
          return 'numeric';
        case 'email':
          return 'email-address';
        default:
          return 'default';
      }
    };

    const getAutoComplete = () => {
      // Android does not support address-level1 and address-level2 and it crashes
      if (Platform.OS !== 'android') {
        return autocomplete;
      }

      if (
        autocomplete === 'address-level1' ||
        autocomplete === 'address-level2'
      ) {
        return 'street-address';
      }

      return autocomplete;
    };

    return (
      <StyledInlineRow
        gutterBottom={gutterBottom}
        bgColor={bgColor}
        numberOfLines={numberOfLines}
        borderColor={borderColor}
        borderBottomColor={borderBottomColor}
        style={customStyles}
        onLayout={onLayout}
      >
        {leftElement}
        <StyledTextInput
          color={color}
          onFocus={onFocus}
          textColor={textColor}
          selectionColor={editable ? undefined : 'transparent'}
          textContentType={textContentType}
          keyboardType={keyboardType()}
          placeholderTextColor={
            placeholderColor ?? OwnableTheme.colors.persianIndigo
          }
          fontSize={fontSizePx}
          secureTextEntry={showPassword ? false : secureTextEntry}
          value={value}
          onChange={onChangeCallback}
          placeholder={placeholder}
          numberOfLines={numberOfLines}
          multiline={numberOfLines > 1}
          onKeyPress={onKeyPress}
          textAlign={textAlign}
          hideCursor={!editable}
          autoCapitalize={autoCapitalize}
          id={id}
          name={name}
          autoComplete={autocomplete}
          autoCompleteType={getAutoComplete()}
          onBlur={() => {
            onBlur();
            if (Platform.OS === 'web') {
              window.scrollTo(0, 0);
            }
          }}
          style={textInputStyle}
        />
        {!secureTextEntry ? (
          rightElement
        ) : (
          <IconContainer
            onPress={() => {
              setShowPassword(!showPassword);
            }}
            // @ts-expect-error
            style={styles.iconContainer}
          >
            <Icon
              icon={showPassword ? 'HidePassword' : 'ShowPassword'}
              cssSize={25}
              cssColor={textColor ?? color ?? OwnableTheme.colors.persianIndigo}
            />
          </IconContainer>
        )}
      </StyledInlineRow>
    );
  }
);
export default TextInput;

const styles = StyleSheet.create({
  eyeIcon: { position: 'absolute', right: 15, top: 10 },
});
